import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBullseye,
  faCalendarDays,
  faChartLine,
  faClockRotateLeft,
  faCloudArrowUp,
  faCompass,
  faFolderOpen,
  faPaperclip,
  faPenToSquare,
  faSearch,
  faTriangleExclamation,
  faCircleCheck
} from '@fortawesome/free-solid-svg-icons';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import swal from 'sweetalert';
import { db, storage } from '../firebase';
import './dash.css';

const COLLECTION_NAME = 'strategicRoadmapGoals';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const EMPTY_FORM = {
  title: '',
  planningLens: 'annual',
  category: '',
  targetDate: '',
  reminderDate: '',
  status: 'planned',
  priority: 'medium',
  progressPercent: '0',
  owner: '',
  successMetric: '',
  description: '',
  visionWhy: '',
  changeNote: '',
  pinToBoard: true,
  file: null
};

const LENS_OPTIONS = [
  { value: 'annual', label: 'Annual', helper: 'Goals for the current year.' },
  { value: 'five-year', label: '5-Year', helper: 'Medium horizon milestones.' },
  { value: 'vision', label: 'Vision', helper: 'Long-range north star goals.' },
  { value: 'custom', label: 'Custom', helper: 'Any roadmap item with a unique timing.' }
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'changed', label: 'Changed' },
  { value: 'paused', label: 'Paused' },
  { value: 'achieved', label: 'Achieved' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const safeFileName = (value) => String(value || 'attachment').replace(/[^a-zA-Z0-9._-]/g, '_');

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
  }

  const nextDate = new Date(value);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

const formatDateInputValue = (value) => {
  const date = parseDateValue(value);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  const date = parseDateValue(value);
  if (!date) return 'Not set';

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (value) => {
  const date = parseDateValue(value);
  if (!date) return 'Not set';

  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return 'Unknown size';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getLensLabel = (lens) => {
  const match = LENS_OPTIONS.find((option) => option.value === lens);
  return match?.label || 'Custom';
};

const getLensHelper = (lens) => {
  const match = LENS_OPTIONS.find((option) => option.value === lens);
  return match?.helper || 'Custom roadmap item.';
};

const getStatusTone = (status) => {
  if (status === 'achieved') return 'success';
  if (status === 'in-progress') return 'primary';
  if (status === 'changed') return 'warning';
  if (status === 'paused') return 'secondary';
  return 'dark';
};

const getPriorityTone = (priority) => {
  if (priority === 'critical') return 'danger';
  if (priority === 'high') return 'warning';
  if (priority === 'medium') return 'primary';
  return 'secondary';
};

const clampProgress = (value) => {
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue)) return 0;
  return Math.max(0, Math.min(100, Math.round(nextValue)));
};

const dateMeta = (value, achieved = false) => {
  const date = parseDateValue(value);

  if (!date) {
    return {
      state: 'no-date',
      tone: 'secondary',
      label: 'No target date',
      detail: 'Date not set',
      daysLeft: null
    };
  }

  if (achieved) {
    return {
      state: 'achieved',
      tone: 'success',
      label: 'Achieved',
      detail: `Completed on ${formatDate(date)}`,
      daysLeft: 0
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDay = new Date(date);
  targetDay.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((targetDay.getTime() - today.getTime()) / MS_PER_DAY);

  if (daysLeft < 0) {
    return {
      state: 'overdue',
      tone: 'danger',
      label: 'Overdue',
      detail: `${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} overdue`,
      daysLeft
    };
  }

  if (daysLeft === 0) {
    return {
      state: 'due-today',
      tone: 'warning',
      label: 'Due today',
      detail: 'Due today',
      daysLeft
    };
  }

  if (daysLeft <= 30) {
    return {
      state: 'due-soon',
      tone: 'warning',
      label: 'Due soon',
      detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      daysLeft
    };
  }

  return {
    state: 'on-track',
    tone: 'success',
    label: 'On track',
    detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
    daysLeft
  };
};

const reminderMeta = (value) => {
  const date = parseDateValue(value);

  if (!date) {
    return {
      state: 'no-reminder',
      tone: 'secondary',
      label: 'No reminder',
      detail: 'No reminder set',
      daysLeft: null
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const reminderDay = new Date(date);
  reminderDay.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((reminderDay.getTime() - today.getTime()) / MS_PER_DAY);

  if (daysLeft < 0) {
    return {
      state: 'reminder-overdue',
      tone: 'danger',
      label: 'Reminder overdue',
      detail: `${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} late`,
      daysLeft
    };
  }

  if (daysLeft === 0) {
    return {
      state: 'reminder-today',
      tone: 'warning',
      label: 'Reminder today',
      detail: 'Reminder today',
      daysLeft
    };
  }

  if (daysLeft <= 14) {
    return {
      state: 'reminder-soon',
      tone: 'warning',
      label: 'Reminder soon',
      detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      daysLeft
    };
  }

  return {
    state: 'reminder-ok',
    tone: 'success',
    label: 'Reminder set',
    detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
    daysLeft
  };
};

const buildChangeSummary = (previousGoal, nextGoal, fileChanged) => {
  const summaries = [];
  const compareFields = [
    ['title', 'Title'],
    ['planningLens', 'Timeline'],
    ['category', 'Category'],
    ['targetDate', 'Target date'],
    ['reminderDate', 'Reminder date'],
    ['status', 'Status'],
    ['priority', 'Priority'],
    ['progressPercent', 'Progress'],
    ['owner', 'Owner'],
    ['successMetric', 'Success metric'],
    ['description', 'Description'],
    ['visionWhy', 'Vision note'],
    ['pinToBoard', 'Pinned state']
  ];

  compareFields.forEach(([field, label]) => {
    const left = field === 'progressPercent'
      ? clampProgress(previousGoal?.[field])
      : Boolean(typeof previousGoal?.[field] === 'boolean' ? previousGoal[field] : String(previousGoal?.[field] || '').trim());
    const right = field === 'progressPercent'
      ? clampProgress(nextGoal?.[field])
      : Boolean(typeof nextGoal?.[field] === 'boolean' ? nextGoal[field] : String(nextGoal?.[field] || '').trim());

    const previousValue = field === 'progressPercent'
      ? clampProgress(previousGoal?.[field])
      : field === 'pinToBoard'
        ? Boolean(previousGoal?.[field])
        : String(previousGoal?.[field] || '').trim();
    const nextValue = field === 'progressPercent'
      ? clampProgress(nextGoal?.[field])
      : field === 'pinToBoard'
        ? Boolean(nextGoal?.[field])
        : String(nextGoal?.[field] || '').trim();

    if (field === 'description' || field === 'visionWhy' || field === 'successMetric') {
      if (String(previousValue || '') !== String(nextValue || '')) {
        summaries.push(`${label} updated`);
      }
      return;
    }

    if (field === 'pinToBoard') {
      if (Boolean(previousValue) !== Boolean(nextValue)) {
        summaries.push(Boolean(nextValue) ? 'Pinned to vision board' : 'Removed from vision board');
      }
      return;
    }

    if (field === 'progressPercent') {
      if (left !== right) {
        summaries.push(`Progress set to ${clampProgress(nextGoal?.[field])}%`);
      }
      return;
    }

    if (String(previousValue || '') !== String(nextValue || '')) {
      if (field === 'targetDate') {
        summaries.push(`Target date changed to ${nextValue ? formatDate(nextValue) : 'not set'}`);
      } else if (field === 'reminderDate') {
        summaries.push(`Reminder date changed to ${nextValue ? formatDate(nextValue) : 'not set'}`);
      } else {
        summaries.push(`${label} updated`);
      }
    }
  });

  if (fileChanged) {
    summaries.push('Attachment replaced');
  }

  return summaries;
};

const latestHistoryEntry = (goal) => {
  const entries = Array.isArray(goal?.changeLog) ? goal.changeLog : [];
  return entries.length > 0 ? entries[entries.length - 1] : null;
};

function StrategicRoadmapComp() {
  const auth = getAuth();
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lensFilter, setLensFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    const roadmapQuery = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(roadmapQuery, (snapshot) => {
      const nextItems = [];
      snapshot.forEach((docItem) => {
        nextItems.push({ id: docItem.id, ...docItem.data() });
      });
      setRoadmapItems(nextItems);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load strategic roadmap items:', error);
      setRoadmapItems([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const goals = useMemo(() => roadmapItems.map((item) => {
    const history = Array.isArray(item.changeLog) ? item.changeLog : [];
    const latestHistory = history.length > 0 ? history[history.length - 1] : null;
    const targetMeta = dateMeta(item.targetDate, item.status === 'achieved');
    const reminderState = reminderMeta(item.reminderDate);
    const progressPercent = clampProgress(item.progressPercent);

    return {
      ...item,
      progressPercent,
      planningLens: item.planningLens || 'custom',
      lensLabel: getLensLabel(item.planningLens),
      lensHelper: getLensHelper(item.planningLens),
      categoryLabel: String(item.category || 'Unsorted').trim() || 'Unsorted',
      targetMeta,
      reminderState,
      history,
      latestHistory,
      pinned: Boolean(item.pinToBoard),
      updatedAtDate: parseDateValue(item.updatedAt),
      createdAtDate: parseDateValue(item.createdAt)
    };
  }), [roadmapItems]);

  const filteredGoals = useMemo(() => {
    const queryText = normalizeText(searchTerm);

    return goals
      .filter((goal) => {
        if (lensFilter !== 'all' && goal.planningLens !== lensFilter) {
          return false;
        }

        if (statusFilter !== 'all' && goal.status !== statusFilter) {
          return false;
        }

        if (priorityFilter !== 'all' && goal.priority !== priorityFilter) {
          return false;
        }

        if (!queryText) {
          return true;
        }

        const haystack = [
          goal.title,
          goal.categoryLabel,
          goal.description,
          goal.owner,
          goal.visionWhy,
          goal.successMetric,
          goal.lensLabel
        ].filter(Boolean).join(' ').toLowerCase();

        return haystack.includes(queryText);
      })
      .sort((left, right) => {
        if (left.pinned !== right.pinned) {
          return left.pinned ? -1 : 1;
        }

        const priorityRank = { critical: 0, high: 1, medium: 2, low: 3 };
        const priorityDiff = priorityRank[left.priority] - priorityRank[right.priority];
        if (priorityDiff !== 0) {
          return priorityDiff;
        }

        const leftDays = left.targetMeta.daysLeft ?? Number.MAX_SAFE_INTEGER;
        const rightDays = right.targetMeta.daysLeft ?? Number.MAX_SAFE_INTEGER;
        if (leftDays !== rightDays) {
          return leftDays - rightDays;
        }

        const leftCreated = left.createdAtDate?.getTime() ?? 0;
        const rightCreated = right.createdAtDate?.getTime() ?? 0;
        return rightCreated - leftCreated;
      });
  }, [goals, searchTerm, lensFilter, priorityFilter, statusFilter]);

  const pinnedGoals = useMemo(() => (
    filteredGoals
      .filter((goal) => goal.pinned)
      .slice(0, 4)
  ), [filteredGoals]);

  const lensGroups = useMemo(() => {
    const groupMap = new Map(LENS_OPTIONS.map((option) => [option.value, []]));
    filteredGoals.forEach((goal) => {
      if (!groupMap.has(goal.planningLens)) {
        groupMap.set(goal.planningLens, []);
      }
      groupMap.get(goal.planningLens).push(goal);
    });

    return LENS_OPTIONS
      .filter((option) => lensFilter === 'all' || lensFilter === option.value)
      .map((option) => ({
        ...option,
        items: groupMap.get(option.value) || []
      }))
      .filter((group) => group.items.length > 0 || lensFilter === group.value);
  }, [filteredGoals, lensFilter]);

  const stats = useMemo(() => {
    const total = goals.length;
    const pinned = goals.filter((goal) => goal.pinned).length;
    const overdue = goals.filter((goal) => ['overdue', 'due-today'].includes(goal.targetMeta.state) && goal.status !== 'achieved').length;
    const dueSoon = goals.filter((goal) => ['due-soon', 'due-today'].includes(goal.targetMeta.state) && goal.status !== 'achieved').length;
    const changed = goals.filter((goal) => latestHistoryEntry(goal)?.timestamp && Date.now() - latestHistoryEntry(goal).timestamp < 30 * MS_PER_DAY).length;
    const achieved = goals.filter((goal) => goal.status === 'achieved').length;

    return { total, pinned, overdue, dueSoon, changed, achieved };
  }, [goals]);

  const spotlightGoal = useMemo(() => {
    if (pinnedGoals.length > 0) {
      return pinnedGoals[0];
    }

    return filteredGoals[0] || null;
  }, [filteredGoals, pinnedGoals]);

  const hasActiveFilters = Boolean(
    searchTerm.trim() || lensFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all'
  );

  const clearFilters = () => {
    setSearchTerm('');
    setLensFilter('all');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  const followUpGoals = useMemo(() => (
    goals
      .filter((goal) => {
        const targetNeedsAttention = ['overdue', 'due-today', 'due-soon'].includes(goal.targetMeta.state);
        const reminderNeedsAttention = ['reminder-overdue', 'reminder-today', 'reminder-soon'].includes(goal.reminderState.state);

        return goal.status !== 'achieved' && (targetNeedsAttention || reminderNeedsAttention);
      })
      .sort((left, right) => {
        const leftScore = left.targetMeta.state === 'overdue' ? 0 : left.targetMeta.state === 'due-today' ? 1 : left.targetMeta.state === 'due-soon' ? 2 : 3;
        const rightScore = right.targetMeta.state === 'overdue' ? 0 : right.targetMeta.state === 'due-today' ? 1 : right.targetMeta.state === 'due-soon' ? 2 : 3;
        if (leftScore !== rightScore) {
          return leftScore - rightScore;
        }
        const leftDays = left.targetMeta.daysLeft ?? Number.MAX_SAFE_INTEGER;
        const rightDays = right.targetMeta.daysLeft ?? Number.MAX_SAFE_INTEGER;
        return leftDays - rightDays;
      })
      .slice(0, 5)
  ), [goals]);

  const openCreateModal = () => {
    if (saving) {
      return;
    }

    setEditingGoal(null);
    setSelectedGoal(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (goal) => {
    if (saving || !goal) {
      return;
    }

    setSelectedGoal(null);
    setEditingGoal(goal);
    setFormData({
      title: goal.title || '',
      planningLens: goal.planningLens || 'annual',
      category: goal.categoryLabel || '',
      targetDate: formatDateInputValue(goal.targetDate),
      reminderDate: formatDateInputValue(goal.reminderDate),
      status: goal.status || 'planned',
      priority: goal.priority || 'medium',
      progressPercent: String(goal.progressPercent ?? 0),
      owner: goal.owner || '',
      successMetric: goal.successMetric || '',
      description: goal.description || '',
      visionWhy: goal.visionWhy || '',
      changeNote: '',
      pinToBoard: Boolean(goal.pinned),
      file: null
    });
    setShowModal(true);
  };

  const openGoalDetails = (goal) => {
    setSelectedGoal(goal);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setShowModal(false);
    setEditingGoal(null);
    setFormData(EMPTY_FORM);
  };

  const setField = (field) => (event) => {
    const value = field === 'file'
      ? event.target.files?.[0] || null
      : field === 'pinToBoard'
        ? event.target.checked
        : event.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // eslint-disable-next-line no-unused-vars
  async function handleSubmit(event) {
    event.preventDefault();

    const title = String(formData.title || '').trim();
    if (!title) {
      swal('Missing goal title', 'Please give this roadmap item a clear title.', 'error');
      return;
    }

    const progressPercent = clampProgress(formData.progressPercent);
    const category = String(formData.category || '').trim();
    const targetDate = formData.targetDate || null;
    const reminderDate = formData.reminderDate || null;
    const changeNote = String(formData.changeNote || '').trim();
    const file = formData.file;
    const hasNewFile = Boolean(file);

    const nextGoal = {
      title,
      planningLens: formData.planningLens,
      category,
      targetDate,
      reminderDate,
      status: formData.status,
      priority: formData.priority,
      progressPercent,
      owner: String(formData.owner || '').trim(),
      successMetric: String(formData.successMetric || '').trim(),
      description: String(formData.description || '').trim(),
      visionWhy: String(formData.visionWhy || '').trim(),
      pinToBoard: Boolean(formData.pinToBoard)
    };

    if (editingGoal) {
      const changeSummary = buildChangeSummary(editingGoal, nextGoal, hasNewFile);

      if (changeSummary.length === 0 && !changeNote && !hasNewFile) {
        swal('No changes', 'There is nothing new to save on this roadmap item.', 'info');
        return;
      }
    }

    if (targetDate && reminderDate) {
      const target = parseDateValue(targetDate);
      const reminder = parseDateValue(reminderDate);
      if (target && reminder && reminder > target) {
        swal('Reminder error', 'The reminder date should be before the target date.', 'error');
        return;
      }
    }

    const goalRef = editingGoal
      ? doc(db, COLLECTION_NAME, editingGoal.id)
      : doc(collection(db, COLLECTION_NAME));

    const storageBase = `strategic-roadmap/${goalRef.id}`;
    const changeEntry = {
      id: String(Date.now()),
      type: editingGoal ? 'updated' : 'created',
      timestamp: Date.now(),
      note: editingGoal
        ? (changeNote || 'Roadmap item updated.')
        : 'Roadmap item created.',
      summary: editingGoal
        ? buildChangeSummary(editingGoal, nextGoal, hasNewFile).join(' • ') || 'General roadmap update.'
        : 'New roadmap item created.',
      author: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin'
    };

    setSaving(true);

    try {
      let attachmentPayload = editingGoal ? {
        attachmentUrl: editingGoal.attachmentUrl || '',
        attachmentPath: editingGoal.attachmentPath || '',
        attachmentName: editingGoal.attachmentName || '',
        attachmentSize: editingGoal.attachmentSize || 0,
        attachmentType: editingGoal.attachmentType || ''
      } : {};

      if (hasNewFile) {
        const path = `${storageBase}/${Date.now()}_${safeFileName(file.name)}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file, {
          contentType: file.type || 'application/octet-stream',
          customMetadata: {
            goalTitle: title,
            category,
            owner: String(formData.owner || '').trim()
          }
        });

        attachmentPayload = {
          attachmentUrl: await getDownloadURL(fileRef),
          attachmentPath: path,
          attachmentName: file.name,
          attachmentSize: file.size,
          attachmentType: file.type || ''
        };
      }

      const payload = {
        title,
        planningLens: formData.planningLens,
        category,
        targetDate,
        reminderDate,
        status: formData.status,
        priority: formData.priority,
        progressPercent,
        owner: String(formData.owner || '').trim(),
        successMetric: String(formData.successMetric || '').trim(),
        description: String(formData.description || '').trim(),
        visionWhy: String(formData.visionWhy || '').trim(),
        pinToBoard: Boolean(formData.pinToBoard),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || '',
        updatedByEmail: auth.currentUser?.email || '',
        ...attachmentPayload
      };

      if (editingGoal) {
        await updateDoc(goalRef, {
          ...payload,
          changeLog: [...(Array.isArray(editingGoal.changeLog) ? editingGoal.changeLog : []), changeEntry]
        });
      } else {
        await setDoc(goalRef, {
          ...payload,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || '',
          createdByEmail: auth.currentUser?.email || '',
          changeLog: [changeEntry]
        });
      }

      setShowModal(false);
      setEditingGoal(null);
      setFormData(EMPTY_FORM);
      swal('Saved', editingGoal ? 'The roadmap item has been updated.' : 'The roadmap item has been added.', 'success');
    } catch (error) {
      console.error('Failed to save strategic roadmap item:', error);
      swal('Save failed', error.message || 'The roadmap item could not be saved right now.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const openAttachment = (goal) => {
    if (goal?.attachmentUrl) {
      window.open(goal.attachmentUrl, '_blank', 'noreferrer');
    }
  };

  return (
    <>
      <div className="clientsComp applicationsPage strategicRoadmapPage">
        <div className="strategicRoadmapHero">
          <div className="strategicRoadmapHeroCopy">
            <p className="appsKicker">Vision, timelines, accountability</p>
            <h1>Strategic Roadmap</h1>
            <p className="appsSubtext">
              Build year-by-year goals, 5-year milestones, and long-range vision cards. Attach quotations, set reminders,
              and keep a living change trail so every plan stays honest.
            </p>
            <div className="strategicRoadmapHeroActions">
              <Button variant="dark" className="strategicRoadmapPrimaryBtn" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faBullseye} />
                <span>Add roadmap item</span>
              </Button>
              <div className="strategicRoadmapHeroHints">
                <span><FontAwesomeIcon icon={faCompass} /> Annual and 5-year planning</span>
                <span><FontAwesomeIcon icon={faPaperclip} /> Attach quotations and briefs</span>
                <span><FontAwesomeIcon icon={faClockRotateLeft} /> Keep change history</span>
              </div>
            </div>
          </div>

          <div className="strategicRoadmapSpotlightCard">
            <p className="strategicRoadmapSpotlightLabel">North star</p>
            <h5>{spotlightGoal?.title || 'Build a future-ready institution'}</h5>
            <p>
              {spotlightGoal?.visionWhy
                || 'Capture the outcomes you want to own, then map them against the dates and evidence that keep everyone accountable.'}
            </p>
            <div className="strategicRoadmapSpotlightMeta">
              <div>
                <span>Status</span>
                <strong>{spotlightGoal ? STATUS_OPTIONS.find((item) => item.value === spotlightGoal.status)?.label || 'Planned' : 'No item yet'}</strong>
              </div>
              <div>
                <span>Target</span>
                <strong>{spotlightGoal ? (spotlightGoal.targetDate ? formatDate(spotlightGoal.targetDate) : 'No date set') : 'Start here'}</strong>
              </div>
              <div>
                <span>Lens</span>
                <strong>{spotlightGoal ? spotlightGoal.lensLabel : 'Strategic'}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="appsHeaderStats strategicRoadmapStats">
          <div className="appsStatChip">
            <strong>{stats.total}</strong>
            <span>Total goals</span>
          </div>
          <div className="appsStatChip">
            <strong>{stats.pinned}</strong>
            <span>Pinned board items</span>
          </div>
          <div className="appsStatChip">
            <strong>{stats.dueSoon}</strong>
            <span>Due soon</span>
          </div>
          <div className="appsStatChip">
            <strong>{stats.overdue}</strong>
            <span>Overdue</span>
          </div>
          <div className="appsStatChip">
            <strong>{stats.changed}</strong>
            <span>Recently changed</span>
          </div>
          <div className="appsStatChip">
            <strong>{stats.achieved}</strong>
            <span>Achieved</span>
          </div>
        </div>

        <div className="appsFiltersCard strategicRoadmapFilters">
          <InputGroup className="appsSearch strategicRoadmapSearch">
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search goals, owners, evidence, or plans"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </InputGroup>

          <Form.Select className="filterSelect strategicRoadmapSelect" value={lensFilter} onChange={(event) => setLensFilter(event.target.value)}>
            <option value="all">All timelines</option>
            {LENS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>

          <Form.Select className="filterSelect strategicRoadmapSelect" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>

          <Form.Select className="filterSelect strategicRoadmapSelect" value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)}>
            <option value="all">All priorities</option>
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
          <div className="strategicRoadmapFilterSummary">
            <span>{filteredGoals.length} of {goals.length} goals</span>
            {hasActiveFilters && <button type="button" onClick={clearFilters}>Clear filters</button>}
          </div>
        </div>

        <div className="strategicRoadmapBoard">
          <section className="appsTableCard strategicRoadmapVisionBoard">
            <div className="appsTableHeader">
              <h6><FontAwesomeIcon icon={faBullseye} /> Vision board</h6>
              <span>{pinnedGoals.length} pinned</span>
            </div>

            {pinnedGoals.length === 0 ? (
              <div className="strategicRoadmapEmptyBoard">
                <FontAwesomeIcon icon={faCompass} />
                <h5>Pin the goals that define the future.</h5>
                <p>
                  Start with the items that matter most, like chairs by a certain date, accreditation milestones, or new campuses.
                </p>
              </div>
            ) : (
              <div className="strategicRoadmapVisionGrid">
                {pinnedGoals.map((goal) => (
                  <article key={goal.id} className={`strategicRoadmapVisionItem strategicRoadmapVisionItem--${goal.status}`}>
                    <div className="strategicRoadmapVisionTop">
                      <Badge bg={getStatusTone(goal.status)}>{STATUS_OPTIONS.find((item) => item.value === goal.status)?.label || goal.status}</Badge>
                      <Badge bg={getPriorityTone(goal.priority)}>{PRIORITY_OPTIONS.find((item) => item.value === goal.priority)?.label || goal.priority}</Badge>
                    </div>
                    <h5>{goal.title}</h5>
                    <p>{goal.visionWhy || goal.description || 'Pinned to the board because it defines the future.'}</p>
                    <div className="strategicRoadmapVisionFoot">
                      <span><FontAwesomeIcon icon={faCalendarDays} /> {goal.targetDate ? formatDate(goal.targetDate) : 'No target date'}</span>
                      <span><FontAwesomeIcon icon={faChartLine} /> {goal.progressPercent}% complete</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="strategicRoadmapTimeline">
            {loading ? (
              <div className="incomeStateCard strategicRoadmapLoading">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading roadmap...</span>
                </Spinner>
                <p>Loading strategic roadmap...</p>
              </div>
            ) : goals.length === 0 ? (
              <div className="strategicRoadmapFirstGoal">
                <span><FontAwesomeIcon icon={faBullseye} /></span>
                <h5>Your roadmap starts with one goal</h5>
                <p>Add a milestone or long-term outcome to begin building the plan.</p>
                <Button variant="dark" onClick={openCreateModal}>Add your first goal</Button>
              </div>
            ) : filteredGoals.length === 0 ? (
              <div className="strategicRoadmapFirstGoal strategicRoadmapFirstGoal--filtered">
                <span><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                <h5>No goals match these filters</h5>
                <p>Clear the filters to see the full roadmap again.</p>
                <Button variant="outline-dark" onClick={clearFilters}>Clear filters</Button>
              </div>
            ) : (
              lensGroups.map((group) => (
                <section key={group.value} className="appsTableCard strategicRoadmapLane">
                  <div className="appsTableHeader strategicRoadmapLaneHeader">
                    <div>
                      <h6>{group.label}</h6>
                      <span>{group.helper}</span>
                    </div>
                    <Badge bg="dark">{group.items.length}</Badge>
                  </div>

                  <div className="strategicRoadmapLaneItems">
                    {group.items.map((goal) => (
                      <article key={goal.id} className={`strategicRoadmapGoalCard strategicRoadmapGoalCard--${goal.status}`} onClick={() => openGoalDetails(goal)}>
                        <div className="strategicRoadmapGoalTop">
                          <div className="strategicRoadmapGoalBadges">
                            <Badge bg={getStatusTone(goal.status)}>{STATUS_OPTIONS.find((item) => item.value === goal.status)?.label || goal.status}</Badge>
                            <Badge bg={getPriorityTone(goal.priority)}>{PRIORITY_OPTIONS.find((item) => item.value === goal.priority)?.label || goal.priority}</Badge>
                            {goal.pinned && <Badge bg="dark">Pinned</Badge>}
                          </div>
                          {goal.targetMeta.state === 'overdue' && <Badge bg="danger">Action needed</Badge>}
                        </div>

                        <div className="strategicRoadmapGoalBody">
                          <h5>{goal.title}</h5>
                          <p>{goal.description || goal.visionWhy || 'No supporting note added yet.'}</p>

                          <div className="strategicRoadmapGoalMeta">
                            <span><FontAwesomeIcon icon={faFolderOpen} /> {goal.categoryLabel}</span>
                            <span><FontAwesomeIcon icon={faCalendarDays} /> {goal.targetDate ? formatDate(goal.targetDate) : 'No date'}</span>
                            <span><FontAwesomeIcon icon={faBullseye} /> {goal.owner || 'No owner'}</span>
                            <span><FontAwesomeIcon icon={faPaperclip} /> {goal.attachmentName || 'No attachment'}</span>
                          </div>

                          <div className="strategicRoadmapProgress">
                            <div className="strategicRoadmapProgressBar">
                              <span style={{ width: `${goal.progressPercent}%` }} />
                            </div>
                            <div className="strategicRoadmapProgressText">
                              <strong>{goal.progressPercent}% complete</strong>
                              <span>{goal.targetMeta.label} · {goal.targetMeta.detail}</span>
                            </div>
                          </div>
                        </div>

                        <div className="strategicRoadmapGoalFooter">
                          <div>
                            <span>{goal.reminderDate ? `Reminder: ${formatDate(goal.reminderDate)}` : 'Reminder not set'}</span>
                            <small>{goal.latestHistory ? `Last change ${formatDateTime(goal.latestHistory.timestamp)}` : 'No changes yet'}</small>
                          </div>
                          <div className="strategicRoadmapGoalActions">
                            <button
                              type="button"
                              className="appsViewBtn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openGoalDetails(goal);
                              }}
                            >
                              Open
                            </button>
                            <button
                              type="button"
                              className="appsViewBtn strategicRoadmapEditBtn"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditModal(goal);
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}

                    {group.items.length === 0 && (
                      <div className="strategicRoadmapEmptyLane">
                        No items yet for this timeline.
                      </div>
                    )}
                  </div>
                </section>
              ))
            )}
          </section>

          <aside className="strategicRoadmapRail">
            <section className="appsTableCard strategicRoadmapPulseCard">
              <div className="appsTableHeader">
                <h6><FontAwesomeIcon icon={faTriangleExclamation} /> Accountability pulse</h6>
                <span>{followUpGoals.length} items</span>
              </div>

              <div className="strategicRoadmapPulseList">
                {followUpGoals.length === 0 ? (
                  <div className="strategicRoadmapEmptyPulse">
                    <FontAwesomeIcon icon={faCircleCheck} />
                    <p>Everything is calm right now. Add a date to start tracking urgency.</p>
                  </div>
                ) : (
                  followUpGoals.map((goal) => (
                    <button
                      key={goal.id}
                      type="button"
                      className="strategicRoadmapPulseItem"
                      onClick={() => openGoalDetails(goal)}
                    >
                      <div>
                        <strong>{goal.title}</strong>
                        <span>{goal.targetMeta.label} · {goal.targetMeta.detail}</span>
                      </div>
                      <Badge bg={goal.targetMeta.tone}>{goal.progressPercent}%</Badge>
                    </button>
                  ))
                )}
              </div>
            </section>

            <section className="appsTableCard strategicRoadmapPulseCard">
              <div className="appsTableHeader">
                <h6><FontAwesomeIcon icon={faClockRotateLeft} /> Recent change trail</h6>
                <span>Latest updates</span>
              </div>

              <div className="strategicRoadmapHistoryList">
                {filteredGoals.slice(0, 4).map((goal) => {
                  const entry = goal.latestHistory;
                  return (
                    <button
                      key={goal.id}
                      type="button"
                      className="strategicRoadmapHistoryItem"
                      onClick={() => openGoalDetails(goal)}
                    >
                      <div>
                        <strong>{goal.title}</strong>
                        <span>{entry ? entry.summary || entry.note : 'No history yet'}</span>
                      </div>
                      <small>{entry ? formatDateTime(entry.timestamp) : formatDateTime(goal.createdAt)}</small>
                    </button>
                  );
                })}
              </div>
            </section>
          </aside>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered size="lg" contentClassName="strategicRoadmapModal">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingGoal ? 'Edit strategic roadmap item' : 'Add strategic roadmap item'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="strategicRoadmapModalIntro">
              <FontAwesomeIcon icon={faCompass} />
              <p>
                Use this space for bold long-range plans, short-term execution targets, or vision markers that must stay visible.
              </p>
            </div>

            <div className="strategicRoadmapFormGrid">
              <Form.Group className="strategicRoadmapWide">
                <Form.Label>Goal title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Buy new chairs by August 23rd"
                  value={formData.title}
                  onChange={setField('title')}
                  required
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Timeline</Form.Label>
                <Form.Select value={formData.planningLens} onChange={setField('planningLens')}>
                  {LENS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Priority</Form.Label>
                <Form.Select value={formData.priority} onChange={setField('priority')}>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select value={formData.status} onChange={setField('status')}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Progress %</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={formData.progressPercent}
                  onChange={setField('progressPercent')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Owner</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Who is accountable?"
                  value={formData.owner}
                  onChange={setField('owner')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Infrastructure, academics, finance, systems..."
                  value={formData.category}
                  onChange={setField('category')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Target date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.targetDate}
                  onChange={setField('targetDate')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Reminder date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.reminderDate}
                  onChange={setField('reminderDate')}
                />
              </Form.Group>

              <Form.Group className="strategicRoadmapWide">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="What exactly needs to happen?"
                  value={formData.description}
                  onChange={setField('description')}
                />
              </Form.Group>

              <Form.Group className="strategicRoadmapWide">
                <Form.Label>Why it matters</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Explain the purpose, the vision, and why the goal must happen."
                  value={formData.visionWhy}
                  onChange={setField('visionWhy')}
                />
              </Form.Group>

              <Form.Group className="strategicRoadmapWide">
                <Form.Label>Success metric</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="How will we know it has worked?"
                  value={formData.successMetric}
                  onChange={setField('successMetric')}
                />
              </Form.Group>

              {editingGoal && (
                <Form.Group className="strategicRoadmapWide">
                  <Form.Label>Change note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Why did the plan change?"
                    value={formData.changeNote}
                    onChange={setField('changeNote')}
                  />
                </Form.Group>
              )}

              <Form.Group className="strategicRoadmapWide">
                <Form.Check
                  type="checkbox"
                  label="Pin this item to the vision board"
                  checked={formData.pinToBoard}
                  onChange={setField('pinToBoard')}
                />
              </Form.Group>

              <Form.Group className="strategicRoadmapWide">
                <Form.Label>{editingGoal ? 'Replace attachment' : 'Attachment / quotation'}</Form.Label>
                <Form.Control
                  key={`${showModal ? 'roadmap-modal-open' : 'roadmap-modal-closed'}-${editingGoal?.id || 'new'}`}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={setField('file')}
                />
                <Form.Text className="text-muted">
                  {editingGoal
                    ? 'Leave this blank to keep the current attachment, or upload a replacement quotation.'
                    : 'Optional: attach a quotation, brief, image, or supporting file.'}
                </Form.Text>
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="dark" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faCloudArrowUp} />}
              <span>{saving ? 'Saving...' : editingGoal ? 'Update roadmap' : 'Save roadmap item'}</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(selectedGoal)} onHide={() => setSelectedGoal(null)} centered size="lg" contentClassName="strategicRoadmapModal">
        {selectedGoal && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedGoal.title || 'Roadmap details'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="strategicRoadmapDetailGrid">
                <div className="strategicRoadmapDetailPane">
                  <span>Timeline</span>
                  <strong>{selectedGoal.lensLabel}</strong>
                  <small>{selectedGoal.lensHelper}</small>
                </div>
                <div className="strategicRoadmapDetailPane">
                  <span>Status</span>
                  <strong>{STATUS_OPTIONS.find((item) => item.value === selectedGoal.status)?.label || selectedGoal.status}</strong>
                  <small>{selectedGoal.targetMeta.label} · {selectedGoal.targetMeta.detail}</small>
                </div>
                <div className="strategicRoadmapDetailPane">
                  <span>Priority</span>
                  <strong>{PRIORITY_OPTIONS.find((item) => item.value === selectedGoal.priority)?.label || selectedGoal.priority}</strong>
                  <small>{selectedGoal.categoryLabel}</small>
                </div>
                <div className="strategicRoadmapDetailPane">
                  <span>Owner</span>
                  <strong>{selectedGoal.owner || 'Not assigned'}</strong>
                  <small>{selectedGoal.pinToBoard ? 'Pinned to vision board' : 'Not pinned'}</small>
                </div>
              </div>

              <div className="strategicRoadmapDetailBlock">
                <span>Description</span>
                <p>{selectedGoal.description || 'No description added.'}</p>
              </div>

              <div className="strategicRoadmapDetailBlock">
                <span>Why it matters</span>
                <p>{selectedGoal.visionWhy || 'No vision note added.'}</p>
              </div>

              <div className="strategicRoadmapDetailMeta">
                <div>
                  <span>Target date</span>
                  <strong>{selectedGoal.targetDate ? formatDate(selectedGoal.targetDate) : 'Not set'}</strong>
                  <small>{selectedGoal.targetMeta.detail}</small>
                </div>
                <div>
                  <span>Reminder date</span>
                  <strong>{selectedGoal.reminderDate ? formatDate(selectedGoal.reminderDate) : 'Not set'}</strong>
                  <small>{selectedGoal.reminderState.detail}</small>
                </div>
                <div>
                  <span>Success metric</span>
                  <strong>{selectedGoal.successMetric || 'Not set'}</strong>
                  <small>{selectedGoal.progressPercent}% complete</small>
                </div>
              </div>

              <div className="strategicRoadmapAttachmentCard">
                <div>
                  <span>Attachment</span>
                  <strong>{selectedGoal.attachmentName || 'No attachment'}</strong>
                  <small>{formatFileSize(selectedGoal.attachmentSize)}</small>
                </div>
                {selectedGoal.attachmentUrl && (
                  <Button
                    as="a"
                    href={selectedGoal.attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="outline-dark"
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    <span>Open file</span>
                  </Button>
                )}
              </div>

              <div className="strategicRoadmapHistoryCard">
                <div className="appsTableHeader">
                  <h6><FontAwesomeIcon icon={faClockRotateLeft} /> Change trail</h6>
                  <span>{Array.isArray(selectedGoal.changeLog) ? selectedGoal.changeLog.length : 0} entries</span>
                </div>
                <div className="strategicRoadmapHistoryTimeline">
                  {(Array.isArray(selectedGoal.changeLog) && selectedGoal.changeLog.length > 0) ? (
                    selectedGoal.changeLog.slice().reverse().map((entry) => (
                      <div key={entry.id} className="strategicRoadmapHistoryEntry">
                        <div className="strategicRoadmapHistoryDot" />
                        <div>
                          <strong>{entry.type === 'created' ? 'Created' : 'Updated'}</strong>
                          <p>{entry.summary || entry.note}</p>
                          <span>{entry.author || 'Unknown'} · {formatDateTime(entry.timestamp)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="strategicRoadmapEmptyHistory">No updates logged yet.</div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              {selectedGoal.attachmentUrl && (
                <Button variant="outline-dark" onClick={() => openAttachment(selectedGoal)}>
                  <FontAwesomeIcon icon={faPaperclip} />
                  <span>Open attachment</span>
                </Button>
              )}
              <Button variant="outline-dark" onClick={() => openEditModal(selectedGoal)}>
                <FontAwesomeIcon icon={faPenToSquare} />
                <span>Edit</span>
              </Button>
              <Button variant="dark" onClick={() => setSelectedGoal(null)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
}

export default StrategicRoadmapComp;
